import { purchase } from 'js-api-client';
import {
  gontijoSessionPrecondition,
  withClientAuth,
} from '@/services/gontijo-session/authorizedPurchaseApi';
import { PaymentPreconditionError, isPaymentPreconditionError } from './paymentPreconditionError';

export { PaymentPreconditionError, isPaymentPreconditionError };

/**
 * Checks run before any payment request. Each returns a `PaymentPreconditionError` to refuse the
 * payment, or null to let it through; the first refusal wins.
 *
 * A static list, not a registry a brand fills in at boot. With registration, removing the one
 * import that registers a guard would drop the guard silently and payments would go out without
 * it. Here, dropping one is a visible line in a diff.
 */
const PAYMENT_PRECONDITIONS = [gontijoSessionPrecondition];

/**
 * Transforms applied to every payment payload, in order. Static for the same reason as above.
 * Each must return the payload untouched when it has nothing to add.
 */
const PAYMENT_PAYLOAD_DECORATORS = [withClientAuth];

/**
 * Creates a payment: the one entry point every payment action goes through.
 *
 * Drop-in for `purchase.createPayment`: same arguments, same return. A refused precondition
 * rejects with its `PaymentPreconditionError` and sends nothing; `paymentFailureHandler` answers it
 * with the error's own `notificationKey` and `followUp`, so the handler stays brand-neutral.
 * @param {string} purchaseToken - The purchase token.
 * @param {Object} payload - The payment payload.
 * @param {Object} polling - Polling callbacks.
 * @returns {Promise} The payment promise.
 */
export function createPayment(purchaseToken, payload, polling) {
  const refusal = PAYMENT_PRECONDITIONS.reduce((found, check) => found || check(), null);
  if (refusal) return Promise.reject(refusal);

  const decorated = PAYMENT_PAYLOAD_DECORATORS.reduce((acc, decorate) => decorate(acc), payload);

  return purchase.createPayment(purchaseToken, decorated, polling);
}
